import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { vehicleRevenueApi, exportApi } from '../services/api';
import { FilePdf, ChartLineUp } from '@phosphor-icons/react';
import { toast } from 'sonner';

const MONTHS_DE_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

const formatEur = (v) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));

const formatDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('de-DE'); } catch (_) { return s.slice(0, 10); }
};

const monthLabel = (yyyymm) => {
  if (!yyyymm || yyyymm.length < 7) return yyyymm;
  const m = parseInt(yyyymm.slice(5, 7), 10);
  return `${MONTHS_DE_SHORT[m - 1]} ${yyyymm.slice(2, 4)}`;
};

const getDefaultRange = () => {
  const now = new Date();
  const y = now.getFullYear();
  return { from: `${y}-01`, to: `${y}-12` };
};

const VehicleRevenueDialog = ({ open, onClose, vehicleId, vehicleLabel }) => {
  const [{ from, to }, setRange] = useState(getDefaultRange());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const res = await vehicleRevenueApi.get(from, to, vehicleId);
      setData(res.data);
    } catch (err) {
      toast.error('Umsatzdaten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [from, to, vehicleId, open]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    try {
      const url = exportApi.pdfVehicleRevenue(from, to, vehicleId, true);
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Download fehlgeschlagen');
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `fahrzeug_umsatz_${from}_${to}.pdf`;
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 2000);
      toast.success('PDF erstellt');
    } catch (_) {
      toast.error('PDF-Export fehlgeschlagen');
    }
  };

  const veh = data?.vehicles?.[0];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="vehicle-revenue-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <ChartLineUp weight="fill" size={22} className="text-[#E60000]" />
            Fahrzeug-Umsatz {vehicleLabel ? `– ${vehicleLabel}` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-4 mb-4">
          <div>
            <Label className="overline mb-1 block">Von</Label>
            <Input
              type="month"
              value={from}
              onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              className="w-44"
              data-testid="vrev-from-input"
            />
          </div>
          <div>
            <Label className="overline mb-1 block">Bis</Label>
            <Input
              type="month"
              value={to}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              className="w-44"
              data-testid="vrev-to-input"
            />
          </div>
          <Button
            onClick={downloadPdf}
            className="btn-primary"
            data-testid="vrev-download-pdf"
          >
            <FilePdf weight="bold" size={16} className="mr-2" />
            Als PDF
          </Button>
        </div>

        {loading && <p className="text-sm text-[#525866]">Lade Umsatzdaten…</p>}

        {!loading && data && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <div className="card-swiss p-3">
                <p className="overline text-[#8A94A6]">Soll (gebucht)</p>
                <p className="text-lg font-bold text-[#111418]" data-testid="vrev-kpi-soll">
                  {formatEur(veh ? veh.totals.soll : data.grand_total.soll)}
                </p>
              </div>
              <div className="card-swiss p-3">
                <p className="overline text-[#8A94A6]">Ist (bezahlt)</p>
                <p className="text-lg font-bold text-[#009951]" data-testid="vrev-kpi-ist">
                  {formatEur(veh ? veh.totals.ist : data.grand_total.ist)}
                </p>
              </div>
              <div className="card-swiss p-3">
                <p className="overline text-[#8A94A6]">Buchungen</p>
                <p className="text-lg font-bold text-[#111418]" data-testid="vrev-kpi-bookings">
                  {veh ? veh.totals.bookings : data.grand_total.bookings}
                </p>
              </div>
              <div className="card-swiss p-3">
                <p className="overline text-[#8A94A6]">Differenz</p>
                <p className={`text-lg font-bold ${((veh ? veh.totals.ist : data.grand_total.ist) - (veh ? veh.totals.soll : data.grand_total.soll)) < 0 ? 'text-[#E60000]' : 'text-[#009951]'}`}>
                  {formatEur((veh ? veh.totals.ist : data.grand_total.ist) - (veh ? veh.totals.soll : data.grand_total.soll))}
                </p>
              </div>
            </div>

            {/* Monthly table for the selected vehicle */}
            {veh && (
              <div className="card-swiss overflow-x-auto mb-5">
                <table className="w-full text-sm">
                  <thead className="bg-[#F7F8FA]">
                    <tr>
                      <th className="text-left p-3 font-semibold">Monat</th>
                      <th className="text-right p-3 font-semibold">Soll</th>
                      <th className="text-right p-3 font-semibold text-[#009951]">Ist</th>
                      <th className="text-right p-3 font-semibold">Buchungen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.months.map((m) => (
                      <tr key={m} className="border-t border-[#E2E4E9]">
                        <td className="p-3">{monthLabel(m)}</td>
                        <td className="p-3 text-right tabular-nums">{formatEur(veh.monthly[m]?.soll)}</td>
                        <td className="p-3 text-right tabular-nums text-[#009951]">{formatEur(veh.monthly[m]?.ist)}</td>
                        <td className="p-3 text-right tabular-nums">{veh.monthly[m]?.bookings || 0}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-[#111418] bg-[#F1F2F4] font-bold">
                      <td className="p-3">Gesamt</td>
                      <td className="p-3 text-right tabular-nums">{formatEur(veh.totals.soll)}</td>
                      <td className="p-3 text-right tabular-nums text-[#009951]">{formatEur(veh.totals.ist)}</td>
                      <td className="p-3 text-right tabular-nums">{veh.totals.bookings}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Booking detail list */}
            {veh && veh.bookings_detail.length > 0 && (
              <div>
                <h3 className="text-sm font-bold mb-2 text-[#111418]">Buchungen im Zeitraum</h3>
                <div className="card-swiss overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F7F8FA]">
                      <tr>
                        <th className="text-left p-3 font-semibold">Buchung</th>
                        <th className="text-left p-3 font-semibold">Von</th>
                        <th className="text-left p-3 font-semibold">Bis</th>
                        <th className="text-right p-3 font-semibold">Tage</th>
                        <th className="text-left p-3 font-semibold">Status</th>
                        <th className="text-right p-3 font-semibold">Anteil</th>
                        <th className="text-right p-3 font-semibold">Gesamtpreis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {veh.bookings_detail.map((b) => (
                        <tr key={b.booking_id} className="border-t border-[#E2E4E9]">
                          <td className="p-3 font-mono text-xs">{b.booking_number || b.booking_id?.slice(0, 8)}</td>
                          <td className="p-3">{formatDate(b.start_date)}</td>
                          <td className="p-3">{formatDate(b.end_date)}</td>
                          <td className="p-3 text-right tabular-nums">{b.days || '—'}</td>
                          <td className="p-3"><span className="text-xs px-2 py-1 rounded bg-[#F7F8FA]">{b.status}</span></td>
                          <td className="p-3 text-right tabular-nums">{formatEur(b.amount_in_range)}</td>
                          <td className="p-3 text-right tabular-nums">{formatEur(b.total_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!veh && (
              <p className="text-sm text-[#8A94A6]">Keine Daten gefunden.</p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VehicleRevenueDialog;
